import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Search,
  Building2,
  Users,
  ChevronRight,
  RefreshCw,
  Eye,
  CircleDashed,
  Zap,
  Rocket,
  Briefcase,
  Crown,
  Gem,
  DollarSign,
  BadgeCheck,
  Hourglass,
  X,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { SortableTableHead, sortData } from '../ui/sortable-table';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLAN_STYLES = {
  none: { label: 'No Plan', Icon: CircleDashed, cls: 'bg-white/10 text-muted-foreground border-white/10' },
  free: { label: 'Free', Icon: CircleDashed, cls: 'bg-slate-500/15 text-slate-300 border-slate-400/25' },
  starter: { label: 'Starter', Icon: Zap, cls: 'bg-sky-500/15 text-sky-400 border-sky-400/30' },
  pro: { label: 'Pro', Icon: Rocket, cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-400/30' },
  business: { label: 'Business', Icon: Briefcase, cls: 'bg-amber-500/15 text-amber-400 border-amber-400/30' },
  done_for_you: { label: 'Done For You', Icon: Crown, cls: 'bg-violet-500/15 text-violet-400 border-violet-400/30' },
  platinum: { label: 'Platinum CFO', Icon: Gem, cls: 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-400/30' },
};

export const PlanEmblem = ({ planId, cadence }) => {
  const style = PLAN_STYLES[planId] || PLAN_STYLES.none;
  const { Icon } = style;
  return (
    <Badge
      variant="outline"
      className={`gap-1.5 font-medium ${style.cls}`}
      data-testid={`plan-emblem-${planId}`}
    >
      <Icon className="h-3 w-3" />
      {style.label}
      {cadence === 'annual' && <span className="opacity-70 text-[10px] uppercase">· Annual</span>}
    </Badge>
  );
};

export const SubStatusBadge = ({ customer }) => {
  if (customer.is_paying) {
    return (
      <Badge className="gap-1 bg-emerald-500/20 text-emerald-400" data-testid="sub-status-paying">
        <BadgeCheck className="h-3 w-3" /> Paying
      </Badge>
    );
  }
  if (customer.is_trialing) {
    return (
      <Badge className="gap-1 bg-blue-500/20 text-blue-400" data-testid="sub-status-trialing">
        <Hourglass className="h-3 w-3" /> Trialing
      </Badge>
    );
  }
  const s = customer.subscription_status;
  if (s === 'past_due') {
    return <Badge className="bg-red-500/20 text-red-400">Past Due</Badge>;
  }
  if (s === 'canceled') {
    return <Badge className="bg-muted/20 text-muted-foreground">Canceled</Badge>;
  }
  return <Badge className="bg-white/10 text-muted-foreground">Free</Badge>;
};

const StatChip = ({ icon: Icon, label, value, accent }) => (
  <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3">
    <div className={`p-2 rounded-lg ${accent}`}>
      <Icon className="h-4 w-4" />
    </div>
    <div>
      <p className="text-lg font-semibold text-white leading-none">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </div>
  </div>
);

export default function OwnerAdminCustomers() {
  const [searchParams] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [payingOnly, setPayingOnly] = useState(false);
  const [showTest, setShowTest] = useState(
    ['1', 'true'].includes(searchParams.get('include_test') || ''),
  );
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState({ key: 'created_at', direction: 'desc' });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const pageSize = 20;

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/owner-admin/customers?skip=${page * pageSize}&limit=${pageSize}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (statusFilter && statusFilter !== 'all') url += `&status=${statusFilter}`;
      if (payingOnly) url += '&paying_only=true';
      if (showTest) url += '&include_test=true';

      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCustomers(data.customers);
        setTotal(data.total);
        setStats(data.stats || null);
      }
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, payingOnly, showTest]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchCustomers();
    }, 300);
    return () => clearTimeout(debounce);
  }, [fetchCustomers]);

  useEffect(() => { setPage(0); }, [search, statusFilter, payingOnly, showTest]);

  const dismissDupe = async (customer, kind) => {
    try {
      const res = await fetch(`${API_URL}/api/owner-admin/duplicates/dismiss`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ owner_email: customer.owner_email, workspace_name: customer.name, kind }),
      });
      if (res.ok) {
        toast.success(kind === 'duplicate' ? 'Duplicate flag dismissed' : 'Same-owner flag dismissed');
        fetchCustomers();
      } else {
        toast.error('Failed to dismiss flag');
      }
    } catch {
      toast.error('Failed to dismiss flag');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || deleteConfirmText !== deleteTarget.name) return;
    setDeleting(true);
    try {
      const res = await fetch(
        `${API_URL}/api/owner-admin/workspaces/${deleteTarget.workspace_id}`,
        { method: 'DELETE', credentials: 'include' },
      );
      const data = await res.json();
      if (res.ok) {
        toast.success(
          `Deleted "${data.workspace_name}" — ${data.total_docs_deleted} records removed permanently`,
        );
        setDeleteTarget(null);
        setDeleteConfirmText('');
        fetchCustomers();
      } else {
        toast.error(data.detail || 'Delete failed');
      }
    } catch {
      toast.error('Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  };

  const sortedCustomers = useMemo(() => sortData(customers, sort), [customers, sort]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Customers</h1>
          <p className="text-muted-foreground">
            {total} customers
            {stats?.hidden_test_count > 0 && !showTest && (
              <span className="text-xs ml-2 opacity-70">({stats.hidden_test_count} test accounts hidden)</span>
            )}
          </p>
        </div>
        <Button variant="outline" onClick={fetchCustomers} className="border-white/20 text-muted-foreground/80" data-testid="customers-refresh-btn">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="customer-stats">
          <StatChip icon={Building2} label="Customers shown" value={total} accent="bg-white/10 text-muted-foreground/80" />
          <StatChip icon={BadgeCheck} label="Paying" value={stats.paying_count} accent="bg-emerald-500/15 text-emerald-400" />
          <StatChip icon={Hourglass} label="Trialing" value={stats.trialing_count} accent="bg-blue-500/15 text-blue-400" />
          <StatChip icon={DollarSign} label="MRR" value={`$${(stats.mrr || 0).toFixed(2)}`} accent="bg-amber-500/15 text-amber-400" />
        </div>
      )}

      {/* Filters */}
      <Card className="bg-card border-white/15">
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by company, email or ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-white/10 border-white/20 text-white"
                data-testid="customer-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px] bg-white/10 border-white/20 text-white" data-testid="customer-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="trial">Trial</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="churned">Churned</SelectItem>
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-sm text-muted-foreground/80 cursor-pointer select-none">
              <Switch checked={payingOnly} onCheckedChange={setPayingOnly} data-testid="paying-only-toggle" />
              Paying only
            </label>
            <label className="flex items-center gap-2 text-sm text-muted-foreground/80 cursor-pointer select-none">
              <Switch checked={showTest} onCheckedChange={setShowTest} data-testid="show-test-toggle" />
              Show test accounts
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Customer Table */}
      <Card className="bg-card border-white/15">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} className="h-12 w-full bg-white/10" />
              ))}
            </div>
          ) : customers.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No customers found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/15 hover:bg-white/[0.07]">
                  <SortableTableHead sortKey="name" currentSort={sort} onSort={setSort}>
                    Company
                  </SortableTableHead>
                  <SortableTableHead sortKey="plan_price" currentSort={sort} onSort={setSort}>
                    Plan
                  </SortableTableHead>
                  <SortableTableHead sortKey="mrr" currentSort={sort} onSort={setSort}>
                    MRR
                  </SortableTableHead>
                  <SortableTableHead sortKey="subscription_status" currentSort={sort} onSort={setSort}>
                    Subscription
                  </SortableTableHead>
                  <SortableTableHead sortKey="user_count" currentSort={sort} onSort={setSort}>
                    Users
                  </SortableTableHead>
                  <SortableTableHead sortKey="created_at" currentSort={sort} onSort={setSort}>
                    Created
                  </SortableTableHead>
                  <TableHead className="text-muted-foreground text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedCustomers.map((customer) => (
                  <TableRow
                    key={customer.workspace_id}
                    className="border-white/15 hover:bg-white/[0.07]"
                    data-testid={`customer-row-${customer.workspace_id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-emerald-500/10">
                          <Building2 className="h-4 w-4 text-emerald-400" />
                        </div>
                        <div>
                          <div className="font-medium text-white flex items-center gap-2">
                            {customer.name}
                            {customer.is_test && (
                              <Badge variant="outline" className="text-[10px] border-orange-400/30 text-orange-400 bg-orange-500/10">TEST</Badge>
                            )}
                            {customer.duplicate_owner_count > 1 && (
                              <Badge
                                variant="outline"
                                data-testid={`customer-dupe-badge-${customer.workspace_id}`}
                                title={`${customer.duplicate_owner_count} workspaces with the SAME business name and owner email — likely double-signup / double-charge.`}
                                className="text-[10px] border-red-400/30 text-red-400 bg-red-500/10 gap-1"
                              >
                                DUPLICATE ×{customer.duplicate_owner_count}
                                <button
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); dismissDupe(customer, 'duplicate'); }}
                                  title="Dismiss — not a duplicate"
                                  className="hover:text-white -mr-0.5"
                                  data-testid={`dismiss-dupe-${customer.workspace_id}`}
                                >
                                  <X className="h-2.5 w-2.5" />
                                </button>
                              </Badge>
                            )}
                            {!(customer.duplicate_owner_count > 1) && customer.same_owner_count > 1 && (
                              <Badge
                                variant="outline"
                                data-testid={`customer-same-owner-badge-${customer.workspace_id}`}
                                title={`This email owns ${customer.same_owner_count} workspaces with different business names — probably separate businesses, shown for awareness.`}
                                className="text-[10px] border-white/20 text-slate-400 bg-white/5 gap-1"
                              >
                                SAME OWNER ×{customer.same_owner_count}
                                <button
                                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); dismissDupe(customer, 'same_owner'); }}
                                  title="Dismiss this flag for this owner"
                                  className="hover:text-white -mr-0.5"
                                  data-testid={`dismiss-same-owner-${customer.workspace_id}`}
                                >
                                  <X className="h-2.5 w-2.5" />
                                </button>
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {customer.owner_email || customer.workspace_id}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <PlanEmblem planId={customer.plan_id} cadence={customer.billing_cadence} />
                    </TableCell>
                    <TableCell className="text-muted-foreground/80">
                      {customer.mrr > 0 ? (
                        <span className="text-emerald-400 font-medium">${customer.mrr.toFixed(2)}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <SubStatusBadge customer={customer} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-muted-foreground/80">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        {customer.user_count || 0}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(customer.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          asChild
                          className="text-muted-foreground hover:text-white"
                        >
                          <Link to={`/owner-admin/customers/${customer.workspace_id}`}>
                            <Eye className="h-4 w-4 mr-1" />
                            View
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={customer.is_paying || customer.is_trialing}
                          title={
                            customer.is_paying || customer.is_trialing
                              ? 'Has an active subscription — cancel it first'
                              : 'Permanently delete this workspace and all its data'
                          }
                          onClick={() => { setDeleteTarget(customer); setDeleteConfirmText(''); }}
                          className="text-muted-foreground hover:text-red-400 disabled:opacity-30"
                          data-testid={`delete-workspace-btn-${customer.workspace_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Permanent-delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) { setDeleteTarget(null); setDeleteConfirmText(''); } }}>
        <DialogContent className="bg-card border-white/15" data-testid="delete-workspace-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              Permanently delete workspace?
            </DialogTitle>
            <DialogDescription className="text-muted-foreground space-y-2">
              This deletes <strong className="text-white">{deleteTarget?.name}</strong>
              {deleteTarget?.owner_email ? ` (owner: ${deleteTarget.owner_email})` : ''} and ALL its
              data — invoices, contacts, journals, members, payment history — from every collection.
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              Type the workspace name <span className="font-mono text-white">{deleteTarget?.name}</span> to confirm:
            </p>
            <Input
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder={deleteTarget?.name}
              className="bg-white/10 border-white/20 text-white"
              data-testid="delete-confirm-input"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setDeleteTarget(null); setDeleteConfirmText(''); }}
              className="border-white/20"
              data-testid="delete-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleting || deleteConfirmText !== deleteTarget?.name}
              onClick={handleDelete}
              data-testid="delete-confirm-btn"
            >
              {deleting ? 'Deleting…' : 'Delete permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pagination */}
      {total > pageSize && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {page * pageSize + 1} - {Math.min((page + 1) * pageSize, total)} of {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="border-white/20 text-muted-foreground/80"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={(page + 1) * pageSize >= total}
              className="border-white/20 text-muted-foreground/80"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
